import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, Download, Loader2, RefreshCw, Share2, Share,
  Sparkles, AlertTriangle, Info, ShieldAlert, ChevronDown, ChevronRight, FilePlus2,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { REPORT_STATUS } from "@/pages/Report";

const NARR_STATUS = {
  GENERATED: { label: "Da revisionare", cls: "bg-amber-100 text-amber-800" },
  EDITED: { label: "Modificata", cls: "bg-sky-100 text-sky-800" },
  APPROVED: { label: "Approvata", cls: "bg-green-100 text-green-800" },
};
const SEVERITY_STYLE = {
  BLOCKING: { cls: "bg-red-100 text-red-800 border-red-300", Icon: ShieldAlert },
  WARNING: { cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: AlertTriangle },
  INFO: { cls: "bg-sky-100 text-sky-800 border-sky-300", Icon: Info },
};

function SectionPreview({ section }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button type="button"
        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900"
        onClick={() => setOpen(!open)}
        data-testid={`section-preview-toggle-${section.code}`}>
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Anteprima dati ({section.items?.length || 0} elementi)
      </button>
      {open && (
        <div className="mt-2 overflow-x-auto rounded-md border border-slate-200"
          data-testid={`section-preview-${section.code}`}>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-left text-slate-500">
                <th className="py-1.5 px-2 font-semibold">Codice</th>
                <th className="py-1.5 px-2 font-semibold">Informazione</th>
                <th className="py-1.5 px-2 font-semibold">Valore</th>
                <th className="py-1.5 px-2 font-semibold">Stato</th>
              </tr>
            </thead>
            <tbody>
              {(section.items || []).map((it) => (
                <tr key={it.code} className="border-t border-slate-100">
                  <td className="py-1.5 px-2 font-mono text-[11px]">{it.code}</td>
                  <td className="py-1.5 px-2">{it.label}</td>
                  <td className="py-1.5 px-2 font-medium">{it.value_display || "—"}</td>
                  <td className="py-1.5 px-2 text-slate-500">{it.status_label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NarrativeCard({ block, section, locked, onAction, busy }) {
  const [text, setText] = useState(block.final_content || "");
  useEffect(() => { setText(block.final_content || ""); }, [block.final_content]);
  const st = NARR_STATUS[block.status] || NARR_STATUS.GENERATED;
  const dirty = text !== (block.final_content || "");
  return (
    <Card className="border-slate-200" data-testid={`narrative-card-${block.section_code}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-bold text-slate-900">
            {block.section_code} — {block.section_title}
          </p>
          <Badge className={st.cls} data-testid={`narrative-status-${block.section_code}`}>{st.label}</Badge>
          <Badge variant="outline" className="text-[10px]"
            data-testid={`narrative-source-${block.section_code}`}>
            {block.source === "AI" ? `AI · ${block.model || ""}` : "Testo automatico"}
          </Badge>
        </div>
        {locked ? (
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
            data-testid={`narrative-text-${block.section_code}`}>{block.final_content}</p>
        ) : (
          <>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
              className="text-sm" data-testid={`narrative-editor-${block.section_code}`}
              aria-label={`Narrazione sezione ${block.section_code}`} />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs"
                disabled={!dirty || busy} onClick={() => onAction("edit", block, text)}
                data-testid={`narrative-save-${block.section_code}`}>Salva modifiche</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs"
                disabled={busy} onClick={() => onAction("regenerate", block)}
                data-testid={`narrative-regenerate-${block.section_code}`}>
                <Sparkles className="w-3 h-3 mr-1" aria-hidden="true" />Rigenera con AI
              </Button>
              {block.status !== "APPROVED" && (
                <Button size="sm" className="h-7 text-xs bg-green-700 hover:bg-green-800 text-white"
                  disabled={busy || dirty} onClick={() => onAction("approve", block)}
                  data-testid={`narrative-approve-${block.section_code}`}>
                  <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Approva sezione
                </Button>
              )}
            </div>
          </>
        )}
        {section && <SectionPreview section={section} />}
      </CardContent>
    </Card>
  );
}

export default function ReportDetail() {
  const { reportId } = useParams();
  const { companyId, period, can } = useTenant();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const canGenerate = can("analysis:report:generate");

  const load = useCallback(async () => {
    if (!companyId || !period?.id || !reportId) return;
    try {
      const { data } = await api.get(
        `/companies/${companyId}/periods/${period.id}/reports/${reportId}`);
      setDetail(data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [companyId, period?.id, reportId]);

  useEffect(() => { load(); }, [load]);

  const sectionsByCode = useMemo(() => {
    const map = {};
    (detail?.snapshot?.sections || []).forEach((s) => { map[s.code] = s; });
    return map;
  }, [detail]);

  const narrativeAction = async (action, block, text) => {
    setBusy(true);
    const base = `/companies/${companyId}/periods/${period.id}/reports/${reportId}/narratives/${block.id}`;
    try {
      if (action === "edit") {
        await api.patch(base, { content: text });
        toast.success("Narrazione aggiornata");
      } else if (action === "regenerate") {
        const { data } = await api.post(`${base}/regenerate`);
        toast.success(data.source === "AI" ? "Narrazione rigenerata con AI"
          : "AI non disponibile: generato testo automatico");
      } else if (action === "approve") {
        await api.post(`${base}/approve`);
        toast.success("Sezione narrativa approvata");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doAction = async (action) => {
    setBusy(true);
    const base = `/companies/${companyId}/periods/${period.id}/reports/${reportId}`;
    try {
      if (action === "approve-all") {
        await api.post(`${base}/narratives/approve-all`);
        toast.success("Tutte le narrazioni approvate");
      } else if (action === "regenerate-pdf") {
        await api.post(`${base}/regenerate-pdf`);
        toast.success("PDF della bozza rigenerato");
      } else if (action === "approve") {
        await api.post(`${base}/approve`);
        toast.success("Report approvato: PDF finale emesso, report bloccato");
      } else if (action === "share") {
        await api.post(`${base}/share`, { shared: true });
        toast.success("Report condiviso con il cliente");
      } else if (action === "unshare") {
        await api.post(`${base}/share`, { shared: false });
        toast.success("Condivisione con il cliente rimossa");
      } else if (action === "new-version") {
        const { data } = await api.post(
          `/companies/${companyId}/periods/${period.id}/reports`,
          { template_code: detail.instance.template_code });
        toast.success(`Nuova versione v${data.instance.version_number} creata in bozza`);
        navigate(`/report/${data.instance.id}`);
        return;
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async (fmt = "pdf") => {
    try {
      const r = await api.get(
        `/companies/${companyId}/periods/${period.id}/reports/${reportId}/download?format=${fmt}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const inst = detail.instance;
      a.href = url;
      a.download = `${inst.template_code}_${period.year}_v${inst.version_number}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (error) {
    return (
      <div className="max-w-lg mx-auto mt-16" data-testid="report-detail-error">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center text-sm text-red-700">{error}</CardContent>
        </Card>
      </div>
    );
  }
  if (!detail) return <p className="text-sm text-slate-500">Caricamento…</p>;

  const inst = detail.instance;
  const st = REPORT_STATUS[inst.status] || REPORT_STATUS.DRAFT;
  const locked = inst.status === "APPROVED";
  const narratives = detail.narratives || [];
  const pendingNarratives = narratives.filter((n) => n.status !== "APPROVED").length;

  return (
    <div className="space-y-6" data-testid="report-detail-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" className="h-8" onClick={() => navigate("/report")}
          data-testid="report-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" />Report
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">
            {inst.template_title} — v{inst.version_number}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Esercizio {period?.year} · Creato il{" "}
            {new Date(inst.created_at).toLocaleDateString("it-IT")}
            {inst.approved_at && ` · Approvato il ${new Date(inst.approved_at).toLocaleDateString("it-IT")}`}
          </p>
        </div>
        <Badge className={st.cls} data-testid="report-detail-status">{st.label}</Badge>
        {inst.visibility === "SHARED_WITH_CLIENT" && (
          <Badge className="bg-indigo-100 text-indigo-800" data-testid="report-detail-shared">
            <Share2 className="w-3 h-3 mr-1" aria-hidden="true" />Condiviso con il cliente
          </Badge>
        )}
      </div>

      {detail.new_data_available && (
        <Card className="border-amber-200 bg-amber-50" data-testid="new-data-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" aria-hidden="true" />
            <p className="text-sm text-amber-900 flex-1">
              I dati ESG sono cambiati dopo la generazione di questo report.
              {locked
                ? " Il report approvato resta immutabile: genera una nuova versione per includere i nuovi dati."
                : " Il contenuto resta congelato sullo snapshot originale."}
            </p>
            {canGenerate && (
              <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                disabled={busy} onClick={() => doAction("new-version")}
                data-testid="new-version-btn">
                <FilePlus2 className="w-3 h-3 mr-1" aria-hidden="true" />Nuova versione
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {(inst.validation_checks || []).length > 0 && (
        <Card className="border-slate-200" data-testid="validation-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Controlli di pre-validazione</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {inst.validation_checks.map((c) => {
              const s = SEVERITY_STYLE[c.severity] || SEVERITY_STYLE.INFO;
              return (
                <div key={c.code} className={`flex items-start gap-2 rounded-md border p-2.5 ${s.cls}`}
                  data-testid={`validation-${c.code}`}>
                  <s.Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                  <p className="text-xs">{c.message}</p>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => download("pdf")}
            data-testid="download-pdf-btn">
            <Download className="w-3 h-3 mr-1" aria-hidden="true" />Scarica PDF
            {!locked && " (bozza)"}
          </Button>
          {canGenerate && (
            <>
              <Button size="sm" variant="outline" className="h-8 text-xs"
                onClick={() => download("docx")} data-testid="download-docx-btn">
                <Download className="w-3 h-3 mr-1" aria-hidden="true" />DOCX
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs"
                onClick={() => download("xlsx")} data-testid="download-xlsx-btn">
                <Download className="w-3 h-3 mr-1" aria-hidden="true" />XLSX
              </Button>
            </>
          )}
          {canGenerate && !locked && (
            <>
              <Button size="sm" variant="outline" className="h-8 text-xs"
                disabled={busy} onClick={() => doAction("regenerate-pdf")}
                data-testid="regenerate-pdf-btn">
                <RefreshCw className="w-3 h-3 mr-1" aria-hidden="true" />Rigenera PDF
              </Button>
              {narratives.length > 0 && pendingNarratives > 0 && (
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  disabled={busy} onClick={() => doAction("approve-all")}
                  data-testid="approve-all-btn">
                  <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />
                  Approva tutte le narrazioni
                </Button>
              )}
              <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
                disabled={busy || pendingNarratives > 0} onClick={() => doAction("approve")}
                data-testid="approve-report-btn"
                title={pendingNarratives > 0
                  ? `${pendingNarratives} sezioni narrative da approvare` : "Approva e blocca"}>
                <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />Approva report
              </Button>
            </>
          )}
          {canGenerate && locked && inst.visibility !== "SHARED_WITH_CLIENT" && (
            <Button size="sm" className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={busy} onClick={() => doAction("share")} data-testid="share-btn">
              <Share className="w-3 h-3 mr-1" aria-hidden="true" />Condividi con il cliente
            </Button>
          )}
          {canGenerate && locked && inst.visibility === "SHARED_WITH_CLIENT" && (
            <Button size="sm" variant="outline" className="h-8 text-xs text-red-700 border-red-300"
              disabled={busy} onClick={() => doAction("unshare")} data-testid="unshare-btn">
              Rimuovi condivisione
            </Button>
          )}
          {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" aria-hidden="true" />}
        </CardContent>
      </Card>

      {narratives.length > 0 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Narrazione del report</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Testi generati dall'AI ancorati ai dati dello snapshot. Revisiona, modifica e
              approva ogni sezione prima dell'approvazione finale.
            </p>
          </div>
          {narratives.map((block) => (
            <NarrativeCard key={block.id} block={block}
              section={sectionsByCode[block.section_code]}
              locked={locked} onAction={narrativeAction} busy={busy} />
          ))}
        </div>
      )}

      {narratives.length === 0 && detail.snapshot && (
        <div className="space-y-4" data-testid="sections-only">
          {(detail.snapshot.sections || []).map((section) => (
            <Card key={section.code} className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-sm font-bold text-slate-900">
                  {section.code} — {section.title}
                </p>
                <SectionPreview section={section} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
